import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { joinDraft } from '../services/api';

/**
 * Commissioner Mode: join a live draft room by its 5-character code, either scanned via the
 * lobby's QR code (landing here with :code pre-filled) or entered manually. A logged-in user joins
 * as a member (no display name needed); an anonymous visitor joins as a guest and gets a private
 * session token (see api.js's storeDraftGuestToken) used for the rest of the draft. This is also
 * how a commissioner realistically joins their own room as a drafter from a second device/browser
 * (e.g. scanning their own QR code on their phone) -- the lobby's own "Join as a Drafter" button
 * only helps if they're already looking at the lobby in that logged-in session.
 */
function DraftJoinView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [joinCode, setJoinCode] = useState(code ? code.toUpperCase() : '');
  const [displayName, setDisplayName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!joinCode.trim()) {
      setError('Enter the 5-character draft code.');
      return;
    }
    if (!isLoggedIn && !displayName.trim()) {
      setError('Enter a display name so other drafters know who you are.');
      return;
    }
    setJoining(true);
    setError(null);
    try {
      const result = await joinDraft(joinCode.trim().toUpperCase(), displayName.trim(), teamName.trim());
      const destination = result.draft.status === 'lobby'
        ? `/draft/${result.draft.draftId}/lobby`
        : `/draft/${result.draft.draftId}`;
      navigate(destination);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not join that draft. Check the code and try again.');
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className='draft-join-container'>
      <h1>Join a Draft</h1>
      <p className='draft-join-subtitle'>
        Enter the code your commissioner shared with you.
      </p>
      <form onSubmit={handleSubmit} className='draft-join-form'>
        <label htmlFor='join-code'>Draft Code</label>
        <input
          id='join-code'
          type='text'
          maxLength={5}
          value={joinCode}
          onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
          placeholder='ABCDE'
          autoFocus
        />
        {!isLoggedIn && (
          <>
            <label htmlFor='display-name'>Your Name</label>
            <input
              id='display-name'
              type='text'
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder='e.g. Alex'
            />
          </>
        )}
        <label htmlFor='team-name'>Team Name (optional)</label>
        <input
          id='team-name'
          type='text'
          maxLength={200}
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          placeholder='e.g. The Gridiron Gurus'
        />
        {error && <div className='draft-join-error'>{error}</div>}
        <button type='submit' className='btn btn-primary' disabled={joining}>
          {joining ? 'Joining…' : 'Join Draft'}
        </button>
      </form>
    </div>
  );
}

export default DraftJoinView;
